import { useState, useEffect, useMemo } from 'react';
import { ClientScheduleData, ScheduleResponse, EventStatus, CalendarDay, ReminderScheduleData, ReminderScheduleResponse, ReminderCalendarDay, ReminderCalendarResponse } from '../types/schedule';
import { apiService } from '../services/api';

export type ActiveTab = 'COBRANÇA' | 'LEMBRETE';

export const useScheduleData = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [scheduleData, setScheduleData] = useState<ClientScheduleData[]>([]);
  const [remindersData, setRemindersData] = useState<ReminderScheduleData[]>([]);
  const [reminderCalendarData, setReminderCalendarData] = useState<ReminderCalendarDay[]>([]);
  const [activeTab, setActiveTab] = useState<ActiveTab>('COBRANÇA');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Estado local para checkboxes (não persiste) - REMOVIDO: agora vem do backend
  const [updatingStatus, setUpdatingStatus] = useState<{[key: string]: boolean}>({});

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  // Buscar dados da API
  const fetchScheduleData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('📅 [useScheduleData] Buscando dados para:', { year, month });
      console.log('📅 [useScheduleData] URL da API:', `/api/billing-rules/schedule/${year}/${month}`);
      
      const response = await apiService.get(
        `/billing-rules/schedule/${year}/${month}`
      ) as ScheduleResponse;
      
      console.log('📅 [useScheduleData] Resposta da API:', response);
      
      if (response.success) {
        setScheduleData(response.data.clients);
        console.log('📅 [useScheduleData] Dados carregados:', response.data.clients.length, 'clientes');
      } else {
        throw new Error('Erro ao carregar dados do cronograma');
      }
    } catch (err) {
      console.error('❌ [useScheduleData] Erro ao buscar dados:', err);
      console.error('❌ [useScheduleData] Tipo do erro:', typeof err);
      console.error('❌ [useScheduleData] Stack do erro:', err instanceof Error ? err.stack : 'N/A');
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  };

  // Buscar dados de lembretes
  const fetchRemindersData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔔 [useScheduleData] Buscando lembretes...');
      
      const response = await apiService.get(
        `/billing-rules/reminders-schedule`
      ) as ReminderScheduleResponse;
      
      console.log('🔔 [useScheduleData] Resposta da API de lembretes:', response);
      
      if (response.success) {
        setRemindersData(response.data.clients);
        console.log('🔔 [useScheduleData] Lembretes carregados:', response.data.clients.length, 'clientes');
      } else {
        throw new Error('Erro ao carregar lembretes');
      }
    } catch (err) {
      console.error('❌ [useScheduleData] Erro ao buscar lembretes:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  };

  // Buscar calendário de lembretes (para mostrar no calendário)
  const fetchReminderCalendar = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('📅 [useScheduleData] Buscando calendário de lembretes para:', { year, month });
      
      const response = await apiService.get(
        `/billing-rules/reminders-calendar/${year}/${month}`
      ) as ReminderCalendarResponse;
      
      console.log('📅 [useScheduleData] Resposta do calendário de lembretes:', response);
      
      if (response.success) {
        setReminderCalendarData(response.data.days);
        console.log('📅 [useScheduleData] Calendário de lembretes carregado:', response.data.days.length, 'dias com lembretes');
      } else {
        throw new Error('Erro ao carregar calendário de lembretes');
      }
    } catch (err) {
      console.error('❌ [useScheduleData] Erro ao buscar calendário de lembretes:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados quando o mês/ano mudar ou quando a aba mudar
  useEffect(() => {
    if (activeTab === 'COBRANÇA') {
      fetchScheduleData();
    } else {
      // Para lembretes, buscar tanto a lista quanto o calendário
      fetchRemindersData();
      fetchReminderCalendar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, activeTab]);

  // Navegar para mês anterior
  const goToPreviousMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  // Navegar para próximo mês
  const goToNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  // Ir para mês atual
  const goToCurrentMonth = () => {
    setCurrentDate(new Date());
  };

  // Atualizar status de um evento
  const updateEventStatus = async (
    clientId: number, 
    eventId: number, 
    eventDate: string, 
    field: 'inProgress' | 'completed', 
    value: boolean
  ) => {
    const statusKey = `${clientId}-${eventId}-${eventDate}`;
    let previousStatus = { is_in_progress: false, is_finalized: false };
    
    try {
      console.log('💾 [useScheduleData] Atualizando status:', { clientId, eventId, eventDate, field, value });
      
      // Marcar como atualizando
      setUpdatingStatus(prev => ({ ...prev, [statusKey]: true }));
      
      // Buscar o status atual do evento para manter os valores existentes
      const day = parseInt(eventDate.split('-')[2]);
      const currentEvent = scheduleData
        .find(client => client.client.id === clientId)
        ?.events[day]
        ?.find(event => event.id === eventId);

      const currentStatus = currentEvent?.status || { is_in_progress: false, is_finalized: false };
      previousStatus = { ...currentStatus };
      
      console.log('🔍 [useScheduleData] Status atual encontrado:', { 
        clientId, 
        eventId, 
        day, 
        currentStatus,
        eventFound: !!currentEvent 
      });

      // Aplicar regras de negócio: checkboxes mutuamente exclusivos
      let newInProgress = currentStatus.is_in_progress;
      let newFinalized = currentStatus.is_finalized;

      if (field === 'inProgress') {
        newInProgress = value;
        // Se marcando "Em andamento", desmarcar "Finalizado"
        if (value) {
          newFinalized = false;
        }
      } else if (field === 'completed') {
        newFinalized = value;
        // Se marcando "Finalizado", desmarcar "Em andamento"
        if (value) {
          newInProgress = false;
        }
      }

      // Preparar dados para a API - sempre enviar ambos os valores
      const updateData = {
        clientId,
        billingRuleId: eventId,
        eventDate,
        isInProgress: newInProgress,
        isFinalized: newFinalized
      };

      // Fazer chamada para API
      console.log('📤 [useScheduleData] Enviando dados para API:', updateData);
      await apiService.updateClientEventStatus(updateData);
      
      console.log('✅ [useScheduleData] Status atualizado com sucesso');
      
      // Atualizar estado local otimisticamente
      setScheduleData(prevData => {
        return prevData.map(clientData => {
          if (clientData.client.id !== clientId) return clientData;
          
          const updatedEvents = { ...clientData.events };
          Object.keys(updatedEvents).forEach(day => {
            updatedEvents[day] = updatedEvents[day].map(event => {
              if (event.id !== eventId) return event;
              
              return {
                ...event,
                status: {
                  ...event.status,
                  is_in_progress: newInProgress,
                  is_finalized: newFinalized,
                  event_date: eventDate
                }
              };
            });
          });
          
          return {
            ...clientData,
            events: updatedEvents
          };
        });
      });
      
    } catch (error) {
      console.error('❌ [useScheduleData] Erro ao atualizar status:', error);
      
      // Reverter mudanças otimistas em caso de erro
      setScheduleData(prevData => {
        return prevData.map(clientData => {
          if (clientData.client.id !== clientId) return clientData;
          
          const updatedEvents = { ...clientData.events };
          Object.keys(updatedEvents).forEach(day => {
            updatedEvents[day] = updatedEvents[day].map(event => {
              if (event.id !== eventId) return event;
              
              // Reverter para o estado anterior
              return {
                ...event,
                status: {
                  ...event.status,
                  is_in_progress: previousStatus.is_in_progress,
                  is_finalized: previousStatus.is_finalized,
                  event_date: eventDate
                }
              };
            });
          });
          
          return {
            ...clientData,
            events: updatedEvents
          };
        });
      });
      
      // Aqui você pode adicionar um toast de erro ou outro feedback visual
      alert('Erro ao atualizar status. Tente novamente.');
    } finally {
      // Remover estado de atualizando
      setUpdatingStatus(prev => ({ ...prev, [statusKey]: false }));
      console.log('🔄 [useScheduleData] Estado de loading resetado para:', statusKey);
    }
  };

  // Obter status de um evento (agora busca do objeto do evento)
  const getEventStatus = (event: any) => {
    if (!event.status) {
      return { inProgress: false, completed: false };
    }
    
    return {
      inProgress: event.status.is_in_progress || false,
      completed: event.status.is_finalized || false
    };
  };


  // Filtrar clientes com eventos no dia atual
  const getClientsForDay = (day: number) => {
    return scheduleData.filter(client => {
      const events = client.events[day];
      return events && events.length > 0;
    });
  };

  // Obter todos os clientes (com e sem eventos no dia atual)
  const getAllClients = () => {
    return scheduleData;
  };

  // Ordenar clientes: primeiro os com eventos no dia atual, depois os demais (alfabético)
  const getSortedClients = (day: number) => {
    const clientsWithEvents = getClientsForDay(day);
    const clientsWithoutEvents = scheduleData.filter(client => {
      const events = client.events[day];
      return !events || events.length === 0;
    });

    // Ordenar cada grupo alfabeticamente
    const sortedWithEvents = clientsWithEvents.sort((a, b) => 
      a.client.legal_name.localeCompare(b.client.legal_name)
    );
    const sortedWithoutEvents = clientsWithoutEvents.sort((a, b) => 
      a.client.legal_name.localeCompare(b.client.legal_name)
    );

    return [...sortedWithEvents, ...sortedWithoutEvents];
  };

  // Gerar dados do calendário
  const calendarDays = useMemo((): CalendarDay[] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // Primeiro dia do mês
    const firstDay = new Date(year, month, 1);
    // Último dia do mês
    const lastDay = new Date(year, month + 1, 0);
    // Primeiro domingo da semana (pode ser do mês anterior)
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days: CalendarDay[] = [];
    const today = new Date();
    
    // Gerar 42 dias (6 semanas)
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const day = date.getDate();
      const isCurrentMonth = date.getMonth() === month;
      const isToday = date.toDateString() === today.toDateString();
      
      let hasEvents = false;
      let eventCount = 0;
      let clientNames: string[] = [];
      
      if (activeTab === 'COBRANÇA') {
        // Verificar se há eventos de cobrança neste dia
        const clientsWithEvents = getClientsForDay(day);
        hasEvents = clientsWithEvents.length > 0;
        eventCount = clientsWithEvents.reduce((total, client) => {
          const events = client.events[day];
          return total + (events ? events.length : 0);
        }, 0);
        
        clientNames = clientsWithEvents.map(client => client.client.trade_name || client.client.legal_name);
      } else if (activeTab === 'LEMBRETE') {
        // Verificar se há lembretes neste dia
        const dayData = reminderCalendarData.find(d => d.day === day);
        if (dayData && dayData.reminders.length > 0) {
          hasEvents = true;
          eventCount = dayData.reminders.length;
          clientNames = dayData.reminders.map(r => r.client.trade_name || r.client.legal_name);
        }
      }
      
      days.push({
        date,
        day,
        isCurrentMonth,
        isToday,
        hasEvents,
        eventCount,
        clients: clientNames
      });
    }
    
    return days;
  }, [currentDate, scheduleData, activeTab, reminderCalendarData]);

  // Obter estatísticas do mês
  const monthStats = useMemo(() => {
    const totalClients = scheduleData.length;
    const clientsWithEvents = scheduleData.filter(client => 
      Object.keys(client.events).length > 0
    ).length;
    const totalEvents = scheduleData.reduce((total, client) => {
      return total + Object.values(client.events).flat().length;
    }, 0);
    
    return {
      totalClients,
      clientsWithEvents,
      clientsWithoutEvents: totalClients - clientsWithEvents,
      totalEvents
    };
  }, [scheduleData]);

  // Obter lembretes de um dia específico (para o calendário)
  const getRemindersByDay = (day: number) => {
    const dayData = reminderCalendarData.find(d => d.day === day);
    return dayData ? dayData.reminders : [];
  };

  return {
    // Estado
    currentDate,
    scheduleData,
    remindersData,
    reminderCalendarData,
    activeTab,
    setActiveTab,
    loading,
    error,
    updatingStatus,
    
    // Ações
    goToPreviousMonth,
    goToNextMonth,
    goToCurrentMonth,
    updateEventStatus,
    getEventStatus,
    refreshData: activeTab === 'COBRANÇA' ? fetchScheduleData : fetchRemindersData,
    
    // Dados processados
    getClientsForDay,
    getAllClients,
    getSortedClients,
    getRemindersByDay,
    calendarDays,
    monthStats,
    
    // Utilitários
    year,
    month
  };
};
